import { isArray, isEmpty } from "lodash"
import React, { useMemo } from "react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlag } from "../../../domain/enums/featureFlags"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import acceptingNewPatientsIcon from "../../../assets/acceptingNewPatients.svg"
import noNewPatientsIcon from "../../../assets/noNewPatients.svg"
import patientWaitlistIcon from "../../../assets/patientWaitlist.svg"
import {
  ProviderLicense,
  ProviderSpecialty,
} from "../../../domain/Provider/schema"
import SubsectionContainer from "../shared/SubsectionContainer"
import { Badge } from "../../shared/components/Badge"
import { useLazyCmsCollectionMap } from "../../../domain/CMS/useLazyCmsCollectionMap"
import { getLicenseStatus, isLicenseStatusWarning } from "../utils/licenseUtils"
import cn from "../../../utils/classes"
import Typography, { typographyTypes } from "../../shared/components/Typography"

const AtAGlanceRow: React.FC<{
  children: React.ReactNode
  center?: boolean
}> = ({ children, center = false }) => {
  return (
    <div className={cn("flex flex-row gap-1", { "items-center": center })}>
      {children}
    </div>
  )
}

interface AtAGlanceContentProps {
  biography?: string
  hasPatientWaitList: boolean
  isAcceptingNewPatients: boolean
  practicingSince?: string
  providerLicenses: ProviderLicense[]
  providerSessionTypeDisplayIds: string[]
  providerSpecialties: ProviderSpecialty[]
  isUnderSupervision: boolean
}

export const AtAGlanceContent: React.FC<AtAGlanceContentProps> = ({
  biography,
  hasPatientWaitList,
  isAcceptingNewPatients,
  practicingSince,
  providerLicenses,
  providerSessionTypeDisplayIds,
  providerSpecialties,
  isUnderSupervision,
}) => {
  const { cms: cmsContent } = useCMSReady([
    "common",
    "provider",
    "publicProfileSlide",
    "providerSpecialtiesMap",
  ])
  const commonText = cmsContent.common
  const providerText = cmsContent.provider
  const publicProfileContent = cmsContent.publicProfileSlide

  const resolvedProviderSpecialtiesMap = useLazyCmsCollectionMap(
    "specialties",
    "providerSpecialtiesMap",
  )
  const providerLicenseTypeOptions = useLazyCmsCollectionMap(
    "licenseTypes",
    "providerQualificationsLicenseTypeMap",
  )

  const isNoVirtualLocationEnabled = useFeatureFlagEnabled(
    FeatureFlag.IsNoVirtualLocationEnabled,
  )

  const resolvedSessionTypesMap = useMemo(() => {
    return {
      inperson: providerText?.sessionsInPerson,
      phone: commonText?.phone,
      telehealth: publicProfileContent?.atAGlanceSubsectionTelehealthLabel,
    }
  }, [commonText, providerText, publicProfileContent])

  const listedSessionTypes = useMemo(() => {
    if (isNoVirtualLocationEnabled) {
      const ids = providerSessionTypeDisplayIds ?? []
      const items: string[] = []
      if (ids.includes("inperson") && providerText?.sessionsInPerson) {
        items.push(providerText.sessionsInPerson)
      }
      if (
        (ids.includes("phone") || ids.includes("telehealth")) &&
        commonText?.virtual
      ) {
        items.push(commonText.virtual)
      }
      items.sort((a, b) => a.localeCompare(b))
      return items.join(", ")
    }

    const hasSessionTypes =
      isArray(providerSessionTypeDisplayIds) &&
      !isEmpty(providerSessionTypeDisplayIds)
    if (!hasSessionTypes) {
      return ""
    }

    const sessionTypes = providerSessionTypeDisplayIds
      .map(
        (sessionTypeDisplayId) =>
          resolvedSessionTypesMap[
            sessionTypeDisplayId as keyof typeof resolvedSessionTypesMap
          ],
      )
      .filter((sessionType) => !!sessionType)
    sessionTypes.sort((a, b) => a?.localeCompare(b ?? "") ?? 0)
    return sessionTypes.join(", ")
  }, [
    isNoVirtualLocationEnabled,
    providerSessionTypeDisplayIds,
    providerText,
    commonText,
    resolvedSessionTypesMap,
  ])

  const yearsInPractice = useMemo(() => {
    const yearStarted = Number(practicingSince)
    if (!isFinite(yearStarted) || !practicingSince) {
      return ""
    }

    const currentYear = Number(new Date().getFullYear())

    if (yearStarted > currentYear) {
      return ""
    }

    const years = currentYear - yearStarted
    return (years === 0 ? 1 : years).toString()
  }, [practicingSince])

  const providerLicensesWithStatus = useMemo(() => {
    const hasLicenses = isArray(providerLicenses) && !isEmpty(providerLicenses)
    if (!hasLicenses) {
      return []
    }

    return providerLicenses.filter((license) => license?.licenseStatusDisplayId)
  }, [providerLicenses])

  const listedPrimarySpecialties = useMemo(() => {
    const hasSpecialties =
      isArray(providerSpecialties) && !isEmpty(providerSpecialties)
    if (!hasSpecialties) {
      return ""
    }

    const specialties = providerSpecialties
      .filter((specialty) => specialty?.specialtyTypeDisplayId === "primary")
      .map(
        (specialty) =>
          resolvedProviderSpecialtiesMap[specialty?.specialtyDisplayId],
      )
      .filter((specialty) => !!specialty)
    specialties.sort((a, b) => a?.localeCompare(b))
    return specialties.join(", ")
  }, [providerSpecialties, resolvedProviderSpecialtiesMap])

  let acceptingPatientsStatusIcon = null
  let acceptingPatientsStatusText = ""
  if (hasPatientWaitList) {
    acceptingPatientsStatusIcon = patientWaitlistIcon
    acceptingPatientsStatusText =
      publicProfileContent?.atAGlanceSubsectionPatientWaitlistText || ""
  } else {
    if (isAcceptingNewPatients) {
      acceptingPatientsStatusIcon = acceptingNewPatientsIcon
      acceptingPatientsStatusText =
        publicProfileContent?.atAGlanceSubsectionAcceptingPatientsText || ""
    } else {
      acceptingPatientsStatusIcon = noNewPatientsIcon
      acceptingPatientsStatusText =
        publicProfileContent?.atAGlanceSubsectionNoNewPatientsText || ""
    }
  }

  return (
    <SubsectionContainer>
      <div className="flex flex-col gap-2">
        {biography && (
          <Typography variant={typographyTypes.NORMAL} text={biography} />
        )}

        {isUnderSupervision && (
          <div className="flex flex-row items-center gap-2 ">
            <Badge
              text={commonText?.underSupervision ?? ""}
              key="1"
              color="info"
              styleOverrides="px-2.5"
            />
          </div>
        )}
        <div className="flex flex-row items-center gap-2">
          <img className="h-5" src={acceptingPatientsStatusIcon} />
          <Typography
            variant={typographyTypes.NORMAL}
            styleOverrides="font-medium"
            text={acceptingPatientsStatusText}
          />
        </div>
        {yearsInPractice && (
          <AtAGlanceRow>
            <Typography
              variant={typographyTypes.NORMAL}
              styleOverrides="font-medium"
              text={
                publicProfileContent?.atAGlancesubsectionYearsPracticingLabel
              }
            />
            <Typography
              variant={typographyTypes.NORMAL}
              text={yearsInPractice}
            />
          </AtAGlanceRow>
        )}
        {providerLicensesWithStatus.map((license, i) => {
          const licenseStatus = getLicenseStatus(license, publicProfileContent)
          return (
            <>
              <AtAGlanceRow key={`license-${i}`}>
                <Typography
                  variant={typographyTypes.NORMAL}
                  styleOverrides="font-medium"
                  text={publicProfileContent?.licenseToolTipLicenseText ?? ""}
                />
                <Typography
                  variant={typographyTypes.NORMAL}
                  text={`${license?.stateCode ?? ""} ${license?.licenseNumber ?? ""}:`}
                />
                <Typography
                  variant={typographyTypes.NORMAL}
                  styleOverrides={
                    isLicenseStatusWarning(license)
                      ? "font-bold !text-aaa-error-red"
                      : ""
                  }
                  text={licenseStatus}
                />
              </AtAGlanceRow>
              <AtAGlanceRow key={`license-type-${i}`}>
                <Typography
                  variant={typographyTypes.NORMAL}
                  styleOverrides="font-medium"
                  text={
                    publicProfileContent?.licenseToolTipLicenseTypeText ?? ""
                  }
                />
                <Typography
                  variant={typographyTypes.NORMAL}
                  text={
                    providerLicenseTypeOptions[license.licenseTypeDisplayId!] ??
                    ""
                  }
                />
              </AtAGlanceRow>
            </>
          )
        })}
        {listedPrimarySpecialties && (
          <AtAGlanceRow>
            <Typography
              variant={typographyTypes.NORMAL}
              styleOverrides="font-medium"
              text={publicProfileContent?.atAGlanceSubsectionSpecialtiesLabel}
            />
            <Typography
              variant={typographyTypes.NORMAL}
              text={listedPrimarySpecialties}
            />
          </AtAGlanceRow>
        )}
        {listedSessionTypes && (
          <AtAGlanceRow>
            <Typography
              variant={typographyTypes.NORMAL}
              styleOverrides="font-medium"
              text={publicProfileContent?.atAGlanceSubsectionCareTypesLabel}
            />
            <Typography
              variant={typographyTypes.NORMAL}
              text={listedSessionTypes}
            />
          </AtAGlanceRow>
        )}
      </div>
    </SubsectionContainer>
  )
}
